/* eslint-disable react/no-unescaped-entities */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import {
  MagnifyingGlass,
  CaretDown,
  ArrowRight,
  BookOpen,
  Info,
  Car as CarIcon,
  Users as UsersIcon,
  Money,
  ChartBar,
  BellRinging,
  ShieldCheck,
  Buildings,
  UserPlus,
  Table,
  Sparkle,
  Question,
} from "@phosphor-icons/react";

// ─── Content ─────────────────────────────────────────────────────────
// Each section: { icon, title, body: JSX }
const SECTIONS = [
  {
    icon: Info,
    title: "نظرة عامة على النظام",
    body: (
      <>
        <p>مراسم الزوار منصة متكاملة لإدارة خدمات النقل الفاخر — تربط الشركات المعتمدة بأسطول السيارات والسائقين، وتُدير عمليات الحجز والتسعير والمحاسبة والإشعارات من مكان واحد.</p>
        <ul>
          <li><b>الأدوار الرئيسية:</b> المدير العام، مدير الحجوزات، الشركة، السائق.</li>
          <li><b>الأسطول:</b> سيارات الشركة (لكزس، جمس، تاهو، سوبربان، …) + سائقون خارجيون.</li>
          <li><b>التسعير:</b> قواعد مسار ثابتة + تجاوزات موسمية + تسعير خاص للشركات.</li>
          <li><b>التقارير:</b> كشوف حسابات، عوائد السيارات، إحصاءات شهرية.</li>
        </ul>
      </>
    ),
  },
  {
    icon: CarIcon,
    title: "كيفية إنشاء حجز جديد",
    body: (
      <>
        <ol>
          <li>اضغط "حجز جديد" من الشاشة الرئيسية أو من الشريط العلوي.</li>
          <li>أدخل اسم العميل ورقم جواله (مع مفتاح الدولة).</li>
          <li>اختر نقطة الانطلاق ونقطة الوجهة من قوائم المدن المدعومة.</li>
          <li>اختر التاريخ (YYYY-MM-DD) والوقت (HH:mm بنظام 24 ساعة).</li>
          <li>اختر نوع السيارة والعدد المطلوب.</li>
          <li>أضف عدد الركاب — إجباري.</li>
          <li>اختياري: أرفق صورة تذكرة الطيران وأضف ملاحظات.</li>
          <li>راجع السعر التلقائي (يتم حسابه من قواعد التسعير) ثم اضغط "تأكيد الحجز".</li>
        </ol>
        <div className="help-tip">💡 يمكنك إضافة عدة مسارات لنفس الحجز (ذهاب/إياب مثلاً) — الأسعار تُحسب لكل مسار على حدة.</div>
      </>
    ),
  },
  {
    icon: UsersIcon,
    title: "تعيين السائقين (مسجل / خارجي)",
    body: (
      <>
        <p>من صفحة الحجوزات، اضغط "تعيين سائق" على أي حجز — يفتح Modal يعرض السائقين المتاحين من نفس نوع السيارة:</p>
        <ul>
          <li><b>السائق المسجل:</b> يظهر مع رقمه ولوحته. اضغط "اختر".</li>
          <li><b>السائق المشغول (بحجز آخر ±3 ساعات):</b> يظهر باللون الأحمر — يمكن الضغط عليه، لكن ستظهر رسالة تحذير قبل التأكيد (رحلة إضافية).</li>
          <li><b>زر واتساب:</b> بجانب كل سائق — يفتح رسالة معاينة بتفاصيل الحجز مباشرة.</li>
          <li><b>السائق الخارجي:</b> تبويب مستقل — أدخل الاسم والجوال والأجرة ثم احفظ. تفتح رسالة واتساب تلقائياً بعد الحفظ.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Money,
    title: "نظام التسعير (المسارات والتجاوزات)",
    body: (
      <>
        <p>يحسب النظام سعر الحجز تلقائياً وفق أولوية:</p>
        <ol>
          <li><b>تسعير خاص للشركة</b> — إن وُجد لهذا المسار والفترة، يُطبَّق أولاً.</li>
          <li><b>تجاوز تاريخي</b> — نطاق تواريخ محدد (موسم عمرة، رمضان، أعياد…).</li>
          <li><b>قاعدة المسار العامة</b> — السعر الأساس لكل مسار حسب نوع السيارة.</li>
        </ol>
        <div className="help-tip">💡 يمكن تعديل قواعد التسعير من <code>/admin/route-pricing</code>، وإضافة تجاوزات موسمية بتاريخ من/إلى.</div>
      </>
    ),
  },
  {
    icon: Table,
    title: "تصدير Excel وتفسير الأعمدة",
    body: (
      <>
        <p>من صفحة "الحجوزات" اضغط "تصدير Excel" — يُنزَّل ملف <code>xlsx</code> يحتوي:</p>
        <ul>
          <li>رقم الحجز · التاريخ · الوقت · المسار · نوع السيارة · العميل · الجوال (مع مفتاح الدولة)</li>
          <li>السائق (مسجل/خارجي) · اللوحة</li>
          <li>السعر · الأجرة · الصافي · الحالة · ملاحظات</li>
        </ul>
        <div className="help-tip">💡 كل الأرقام في الملف لاتينية (0-9) وموحّدة الترميز لتفتح بأي إصدار Excel.</div>
      </>
    ),
  },
  {
    icon: ChartBar,
    title: "التقارير المالية والكشوف",
    body: (
      <>
        <p>تجد التقارير في:</p>
        <ul>
          <li><b><code>/admin/statements</code></b> — كشوف الشركات والسائقين (مدين/دائن، رصيد الفترة).</li>
          <li><b><code>/admin/car-revenue</code></b> — عوائد كل سيارة خلال فترة محددة.</li>
          <li><b><code>/admin/finance</code></b> — ملخص مالي شامل (إجمالي المبيعات، الأجور، الصافي).</li>
        </ul>
      </>
    ),
  },
  {
    icon: BellRinging,
    title: "نظام الإشعارات وإعداداته",
    body: (
      <>
        <p>من <code>/admin/notifications</code> يمكنك:</p>
        <ul>
          <li>تفعيل/تعطيل قنوات الإشعار (Toast داخلي، صوت، إشعار متصفح).</li>
          <li>تحديد الأحداث التي تُطلق إشعاراً (حجز جديد، تعيين سائق، إلغاء…).</li>
          <li>مراجعة سجل الإشعارات في تبويب "Log".</li>
        </ul>
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: "الأدوار والصلاحيات",
    body: (
      <>
        <ul>
          <li><b>المدير العام:</b> صلاحية كاملة — إعدادات، تسعير، شركات، كشوف، حذف.</li>
          <li><b>مدير الحجوزات:</b> نفس صلاحيات المدير العام لإدارة اليومية.</li>
          <li><b>الشركة:</b> إنشاء حجوزات لعملائها، متابعة كشف حسابها، تصدير بياناتها.</li>
          <li><b>السائق:</b> عرض رحلاته وتحديث حالته، بدون وصول للتسعير.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Buildings,
    title: "إدارة الشركات والتسعير الخاص",
    body: (
      <>
        <p>من <code>/admin/companies</code> يمكنك:</p>
        <ol>
          <li>إضافة شركة جديدة (اسم، جوال، بريد، كلمة مرور).</li>
          <li>الدخول إلى صفحة تفاصيل الشركة لعرض حجوزاتها ورصيدها.</li>
          <li>إضافة "تسعير خاص" لمسار محدد يُطبَّق فقط لهذه الشركة خلال نطاق تواريخ.</li>
        </ol>
      </>
    ),
  },
  {
    icon: UserPlus,
    title: "تسجيل السائقين والموافقة",
    body: (
      <>
        <p>السائق يستطيع التسجيل ذاتياً من صفحة <code>/driver-register</code> بإدخال: الاسم، الجوال، رقم اللوحة، نوع السيارة.</p>
        <p>يظهر الطلب في <code>/admin/drivers</code> ← تبويب "طلبات الانتظار" — مع Badge أحمر يعرض عدد الطلبات.</p>
        <ol>
          <li>راجع بيانات السائق واضغط "قبول".</li>
          <li>يُنشأ رمز 6 أرقام في الخادم تلقائياً.</li>
          <li>يُفتح واتساب برسالة ترحيب تحتوي الرمز وخطوات الدخول.</li>
          <li>يمكن لاحقاً عرض الرمز، إعادة إرسال الرسالة، أو تجديد الرمز.</li>
        </ol>
        <div className="help-tip">💡 رمز الوصول سرّي — عرضه محمي بصلاحية المدير فقط، ويمكن تجديده في أي وقت.</div>
      </>
    ),
  },
  {
    icon: CarIcon,
    title: "أرباح السيارات",
    body: (
      <>
        <p>صفحة <code>/admin/car-revenue</code> تعرض لكل سيارة:</p>
        <ul>
          <li>عدد الرحلات · إجمالي الإيراد · إجمالي الأجور · الصافي</li>
          <li>فلترة بفترة تاريخية (اليوم، الأسبوع، الشهر، مخصص).</li>
          <li>ترتيب تنازلي حسب الصافي لتحديد السيارات الأكثر ربحية.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Question,
    title: "أسئلة شائعة (FAQ)",
    body: (
      <>
        <dl>
          <dt><b>لماذا لا تظهر أرقامي بلغة عربية؟</b></dt>
          <dd>النظام يستخدم أرقاماً لاتينية (0-9) في كل مكان لتوحيد المظهر وتفادي مشاكل تصدير Excel وقواعد البيانات.</dd>
          <dt><b>كيف أرفع سعر مسار معين خلال موسم؟</b></dt>
          <dd>من <code>/admin/route-pricing</code> ← أضف "تجاوز" (Override) بنطاق تواريخ وسعر جديد.</dd>
          <dt><b>ماذا يحدث عند تعيين سائق مشغول؟</b></dt>
          <dd>يظهر تحذير أحمر، وعند التأكيد يُسجَّل الحجز مع علامة "رحلة مضاعفة" لتنبيه الفريق.</dd>
          <dt><b>هل يمكن استعادة حجز ملغى؟</b></dt>
          <dd>نعم — من صفحة تفاصيل الحجز، غيّر الحالة يدوياً (تحرير) وأعد التأكيد.</dd>
        </dl>
      </>
    ),
  },
];

// ─── Component ───────────────────────────────────────────────────────
function AccordionItem({ section, defaultOpen = false, matched = false }) {
  const [open, setOpen] = useState(defaultOpen || matched);
  const Icon = section.icon;
  return (
    <div className={`help-section ${matched ? "help-matched" : ""}`}>
      <button
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        data-testid={`help-toggle-${section.title}`}
        className="w-full flex items-center justify-between p-4 text-right"
      >
        <span className="inline-flex items-center gap-2 text-primary font-bold">
          <Icon size={18} weight="duotone" />
          <span>{section.title}</span>
        </span>
        <CaretDown
          size={16}
          weight="bold"
          className={`text-primary transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="help-content px-5 pb-5">{section.body}</div>}
    </div>
  );
}

export default function HelpPage() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return SECTIONS.map((s) => ({ s, matched: false }));
    return SECTIONS.map((s) => {
      const inTitle = s.title.toLowerCase().includes(query);
      // body text: JSX — extract quickly by rendering to string
      const bodyText = JSON.stringify(s.body).toLowerCase();
      return { s, matched: inTitle || bodyText.includes(query) };
    }).filter((x) => x.matched);
  }, [q]);

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/admin"
            className="w-9 h-9 rounded-full border border-primary/30 text-primary hover:bg-primary/10 inline-flex items-center justify-center"
            title="رجوع"
            data-testid="help-back"
          >
            <ArrowRight size={16} weight="bold" />
          </Link>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white inline-flex items-center gap-2">
              <BookOpen size={26} weight="duotone" className="text-primary" />
              دليل استخدام النظام
            </h1>
            <p className="text-white/50 text-sm mt-1">شرح شامل لكل وظائف المنصة — استخدم البحث للوصول السريع.</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <MagnifyingGlass size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-primary/60" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث في الدليل…"
            className="w-full pr-11 pl-4 py-3 rounded-xl bg-card border border-primary/25 text-white placeholder:text-white/40 focus:border-primary outline-none"
            data-testid="help-search"
          />
        </div>

        {/* Sections */}
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-white/50 bg-card border border-primary/15 rounded-2xl">
            لا توجد نتائج تطابق "{q}"
          </div>
        ) : (
          <div className="space-y-3" data-testid="help-sections">
            {filtered.map(({ s, matched }, i) => (
              <AccordionItem key={s.title} section={s} defaultOpen={i === 0 && !q} matched={matched && !!q} />
            ))}
          </div>
        )}

        {/* Footer callout */}
        <div className="mt-8 rounded-2xl p-5 bg-primary/8 border border-primary/25 text-center">
          <Sparkle size={22} weight="fill" className="text-primary mx-auto mb-2" />
          <div className="text-white font-bold text-sm">لم تجد ما تبحث عنه؟</div>
          <div className="text-white/60 text-xs mt-1">
            تواصل مع الإدارة عبر <Link to="/about" className="text-primary underline underline-offset-2">صفحة "عن الشركة"</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
